import { useCallback, useEffect, useState } from 'react';
import { MyInfoPage } from '../../features/my-info/shared/MyInfoPage.jsx';
import { preloadNikkeCatalog } from '../../features/nikke-catalog/hooks/useNikkeCatalog.js';
import { useNavigationBadges } from '../../features/navigation-badges/hooks/useNavigationBadges.js';
import { MobilePlatformUnionReview } from '../../features/platform-union-review/mobile/MobilePlatformUnionReview.jsx';
import { MobileRaidBridge } from '../../features/raid-bridge/mobile/MobileRaidBridge.jsx';
import { MobileRaidOverview } from '../../features/raid-overview/mobile/MobileRaidOverview.jsx';
import { MobileRaidPractice } from '../../features/raid-practice/mobile/MobileRaidPractice.jsx';
import { MobileRaidSettlement } from '../../features/raid-settlement/mobile/MobileRaidSettlement.jsx';
import { RaidControlRoom } from '../../features/raid-control/shared/RaidControlRoom.jsx';
import { MobileRaidSeason } from '../../features/raid-season/mobile/MobileRaidSeason.jsx';
import { RaidStrategyPage } from '../../features/raid-tetris/shared/RaidStrategyPage.jsx';
import { RecommendedDeckBoard } from '../../features/recommended-decks/shared/RecommendedDeckBoard.jsx';
import { MobileUnionMemberManagement } from '../../features/union-member-management/mobile/MobileUnionMemberManagement.jsx';
import { MobileUnionNotices } from '../../features/union-notices/mobile/MobileUnionNotices.jsx';
import { SiteSupportBoard } from '../../features/union-support/shared/UnionSupportBoard.jsx';
import { canManageUnion as hasUnionManagementRole, canManageUnionMemberData } from '../../shared/domain/unionAccess.js';
import { DororaidIcon } from '../../shared/ui/DororaidIcon.jsx';
import { SiteFooter } from '../../shared/ui/SiteFooter.jsx';
import { BroadcastEntryButton } from '../../features/broadcast/shared/BroadcastEntryButton.jsx';
import { SupabaseStatus } from '../../shared/ui/SupabaseStatus.jsx';

const ROUTES = new Set([
  'raid', 'control', 'practice', 'decks', 'tetris', 'notice', 'support', 'settlement',
  'my-info', 'my-nikke', 'account', 'more', 'members', 'management',
  'management/season', 'management/bridge', 'management/support', 'management/platform',
]);

const BOTTOM_NAV_ITEMS = [
  { id: 'raid', label: '레이드', icon: 'raid' },
  { id: 'practice', label: '모의전', icon: 'practice' },
  { id: 'decks', label: '추천덱', icon: 'decks' },
  { id: 'tetris', label: '테트리스', icon: 'tetris' },
  { id: 'notice', label: '공지', icon: 'notice' },
  { id: 'settlement', label: '결산', icon: 'settlement' },
];

const PAGE_META = {
  raid: { title: '레이드 현황' },
  control: { title: '레이드 지휘실' },
  practice: { title: '내 모의전' },
  decks: { title: '추천덱' },
  tetris: { title: '테트리스' },
  notice: { title: '공지' },
  support: { title: '사이트 문의' },
  settlement: { title: '결산' },
  'my-info': { title: '내 정보' },
  'my-nikke': { title: '내 니케' },
  account: { title: '계정 관리' },
  more: { title: '내 정보' },
  members: { title: '멤버', back: 'my-info' },
  management: { title: '관리', back: 'my-info' },
  'management/season': { title: '차수·보스 설정', back: 'management' },
  'management/bridge': { title: '레이드 수집기', back: 'management' },
  'management/support': { title: '사이트 문의 관리', back: 'management' },
  'management/platform': { title: '플랫폼 관리', back: 'management' },
};

function readRouteFromHash() {
  if (typeof window === 'undefined') return 'raid';
  const route = window.location.hash.replace(/^#\/?/, '').split('?')[0].replace(/\/+$/, '');
  return ROUTES.has(route) ? route : 'raid';
}

function MobileFeaturePlaceholder({ title, description }) {
  return <section className="mobile-feature-placeholder" role="status"><div aria-hidden="true">◇</div><span>PAGE READY</span><h2>{title}</h2><p>{description}</p></section>;
}

function AlertBadge({ value, className = 'nav-alert-badge' }) {
  if (!value) return null;
  return <span className={className} aria-label={value === '!' ? '새 알림' : `${value}개 알림`}>{value}</span>;
}

function IconBadgeAnchor({ icon, badge, className = 'nav-icon-badge-anchor' }) {
  return <span className={className}><DororaidIcon name={icon} /><AlertBadge value={badge} /></span>;
}

function MobileMenu({ items, onNavigate }) {
  return <section className="mobile-menu-grid" aria-label="세부 메뉴">{items.map((item) => <button key={item.id} type="button" onClick={() => onNavigate(item.id)}><IconBadgeAnchor icon={item.icon} badge={item.badge} /><div><strong>{item.title}</strong><p>{item.description}</p></div><b aria-hidden="true"><DororaidIcon name="chevronRight" size={18} /></b></button>)}</section>;
}

export function MobileShell({ user, onSignOut, entry }) {
  useEffect(() => {
    const run = () => preloadNikkeCatalog().catch(() => {});
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(run, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timerId = window.setTimeout(run, 0);
    return () => window.clearTimeout(timerId);
  }, []);
  const unionId = String(entry?.union?.id || '');
  const unionName = entry?.union?.name || entry?.profile?.guild_name || '내 유니온';
  const nickname = String(entry?.profile?.nickname || user?.email || '내 정보').trim();
  const [page, setPage] = useState(readRouteFromHash);
  const role = String(entry?.membership?.role || '');
  const canManageUnion = hasUnionManagementRole(role);
  const canManageMemberData = canManageUnionMemberData(role);
  const canManagePlatform = Boolean(entry?.isPlatformAdmin);
  const canOpenManagement = canManageUnion || canManagePlatform;
  const badges = useNavigationBadges({ page, unionId: String(entry?.union?.id || ''), userId: String(user?.id || ''), canManagePlatform });
  const meta = PAGE_META[page] || PAGE_META.raid;
  const hasBack = Boolean(meta.back);

  const navigate = useCallback((nextPage) => {
    const normalized = ROUTES.has(nextPage) ? nextPage : 'raid';
    setPage(normalized);
    if (window.location.hash !== `#/${normalized}`) window.location.hash = `/${normalized}`;
  }, []);

  useEffect(() => { const handler = () => setPage(readRouteFromHash()); window.addEventListener('hashchange', handler); return () => window.removeEventListener('hashchange', handler); }, []);
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); document.title = `${meta.title} · ${unionName} · DoroParty`; }, [meta.title, page, unionName]);

  let content = null;
  if (page === 'management/support' && !canManagePlatform) content = <MobileFeaturePlaceholder title="플랫폼 관리자 권한이 없습니다" description="사이트 문의 관리는 DoroParty 플랫폼 관리자만 사용할 수 있습니다." />;
  else if (page.startsWith('management') && !canOpenManagement) content = <MobileFeaturePlaceholder title="관리 권한이 없습니다" description="주딱 또는 파딱에게 관리 권한을 확인해주세요." />;
  else if (page === 'members' && !canManageUnion) content = <MobileFeaturePlaceholder title="멤버 관리 권한이 없습니다" description="멤버 관리 화면은 운영 권한 계정만 사용할 수 있습니다." />;
  else if (page === 'raid') content = <MobileRaidOverview canManage={canManageUnion} onNavigate={navigate} />;
  else if (page === 'control') content = <RaidControlRoom mobile user={user} unionName={unionName} />;
  else if (page === 'practice') content = <MobileRaidPractice canManage={canManageMemberData} />;
  else if (page === 'decks') content = <RecommendedDeckBoard mobile canManage={canManageUnion} unionId={unionId} unionName={unionName} viewerRole={role} />;
  else if (page === 'tetris') content = <RaidStrategyPage mobile />;
  else if (page === 'notice') content = <MobileUnionNotices onNavigate={navigate} />;
  else if (page === 'support') content = <SiteSupportBoard mobile />;
  else if (page === 'settlement') content = <MobileRaidSettlement />;
  else if (['my-info', 'my-nikke', 'account', 'more'].includes(page)) content = <MyInfoPage mobile user={user} entry={entry} onSignOut={onSignOut} onNavigate={navigate} canManageUnion={canManageUnion} canManageMemberData={canManageMemberData} canOpenManagement={canOpenManagement} initialSection={page === 'account' ? 'account' : page === 'my-nikke' ? 'nikke' : 'overview'} />;
  else if (page === 'members') content = <MobileUnionMemberManagement />;
  else if (page === 'management') content = <MobileMenu items={[
    ...(canManageUnion ? [
      { id: 'members', icon: 'members', title: '멤버 관리', description: '유니온 멤버와 권한 확인' },
      { id: 'management/season', icon: 'season', title: '차수·보스 설정', description: '레이드 차수와 보스 정보 관리' },
      { id: 'management/bridge', icon: 'bridge', title: '레이드 수집기', description: '실전 기록 자동 반영 설정' },
    ] : []),
    ...(canManagePlatform ? [
      { id: 'management/support', icon: 'notice', title: '사이트 문의 관리', description: 'DoroParty 이용자 문의 확인과 답변', badge: badges.supportCount || '' },
      { id: 'management/platform', icon: 'platform', title: '플랫폼 관리', description: '유니온 심사·계정 및 식별자 밴 관리', badge: badges.platformCount || '' },
    ] : []),
  ]} onNavigate={navigate} />;
  else if (page === 'management/season') content = <MobileRaidSeason />;
  else if (page === 'management/bridge') content = <MobileRaidBridge />;
  else if (page === 'management/support') content = <SiteSupportBoard mobile manage />;
  else if (page === 'management/platform') content = <MobilePlatformUnionReview />;

  const bottomActivePage = BOTTOM_NAV_ITEMS.some((item) => item.id === page) ? page : '';
  const routeClass = `mobile-route-${page.replace(/\//g, '-')}`;

  return (
    <div className={`mobile-shell ${routeClass} ${page === 'my-nikke' ? 'personal-nikke-shell' : ''}`.trim()}>
      <header className="mobile-topbar">
        <button className={hasBack ? 'mobile-back-button' : 'mobile-brand-mark'} type="button" onClick={() => navigate(hasBack ? meta.back : 'raid')} aria-label={hasBack ? `${PAGE_META[meta.back]?.title || '이전 화면'}으로 돌아가기` : '레이드 현황으로 이동'}>
          <DororaidIcon name={hasBack ? 'chevronLeft' : 'raid'} size={21} />
        </button>
        <div className="mobile-titlebox"><strong>{meta.title}</strong><span>{unionName}</span></div>
        <div className="mobile-topbar-actions"><SupabaseStatus compact /><BroadcastEntryButton variant="nav" /><button className={page === 'control' ? 'mobile-raid-control-entry active' : 'mobile-raid-control-entry'} type="button" onClick={() => navigate('control')} aria-label="레이드 지휘실 열기" title="레이드 지휘실"><span className="nav-icon-badge-anchor"><DororaidIcon name="chat" size={18} /><AlertBadge value={badges.raidControlUnread ? '!' : ''} /></span></button></div>
        <button className="mobile-profile-entry" type="button" onClick={() => navigate('my-info')} aria-label="내 정보 열기" title="내 정보"><span className="nav-profile-badge-anchor">{nickname.slice(0, 1)}{canOpenManagement ? <AlertBadge value={badges.managementCount || ''} /> : null}</span></button>
      </header>
      <main className="mobile-main mobile-page-main mobile-integrated-page">
        <div className="mobile-page-content integrated">{content}</div>
      </main>
      <SiteFooter compact />
      <nav className="mobile-bottom-nav" aria-label="모바일 주요 메뉴">
        {BOTTOM_NAV_ITEMS.map((item) => <button className={bottomActivePage === item.id ? 'active' : ''} key={item.id} type="button" onClick={() => navigate(item.id)} aria-current={bottomActivePage === item.id ? 'page' : undefined}><IconBadgeAnchor className="mobile-bottom-nav-icon nav-icon-badge-anchor" icon={item.icon} badge={item.id === 'notice' && badges.noticeUnread ? '!' : ''} /><span className="mobile-bottom-nav-label">{item.label}</span></button>)}
      </nav>
    </div>
  );
}
